/**
 * VoiceBar - режим голосового разговора
 *
 * Структура: [BarEqualizer] [Status Text] ... [Expand Button] [Close Button]
 * - Эквалайзер слева
 * - Текст статуса по центру (Speaking/Listening/Connecting)
 * - Кнопки расширения и закрытия справа (как в CollapsedBar)
 */

import { memo } from 'react';
import PropTypes from 'prop-types';
import { X, ArrowsOut as Maximize2 } from '@phosphor-icons/react';
import { BarEqualizer } from './BarEqualizer';
import { AnimatedDots } from './AnimatedDots';
import { t } from './translations';
import { getStatusText, connectionStatusPropType, languagePropType } from './utils';
import './VoiceBar.css';

export const VoiceBar = memo(({
  isSpeaking = false,
  isUserSpeaking = false,
  connectionStatus = 'connected', // 'connecting' | 'connected' | 'disconnected'
  onClose,
  onExpandToChat,
  language = 'en', // 'en' | 'ru' | 'nl'
  glassEffect = false,
  primaryColor,
  theme,
  isAccentMode = false,
}) => {
  const statusText = getStatusText({ connectionStatus, isSpeaking, isUserSpeaking, language });
  const barClassName = `voice-bar${glassEffect ? ' voice-bar--glass' : ''}`;

  return (
    <div className={barClassName}>
      {/* Left side: BarEqualizer icon */}
      <div className="voice-bar__icon">
        <BarEqualizer
          isSpeaking={isSpeaking}
          isUserSpeaking={isUserSpeaking}
          primaryColor={primaryColor}
          theme={theme}
          isAccentMode={isAccentMode}
          width={40}
          height={40}
        />
      </div>

      {/* Status text with animated dots */}
      <div className="voice-bar__status">
        {statusText}
        <AnimatedDots />
      </div>

      {/* Right side: action buttons (like CollapsedBar) */}
      <div className="voice-bar__actions">
        {/* Expand to chat button - visible on hover only */}
        {onExpandToChat && (
          <button
            className="voice-bar__btn voice-bar__btn--expand"
            onClick={onExpandToChat}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            aria-label={t('openChat', language)}
            title={t('openChat', language)}
          >
            <Maximize2 size={18} weight="regular" />
          </button>
        )}

        {/* Close button */}
        <button
          className="voice-bar__btn"
          onClick={onClose}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          aria-label={t('endConversation', language)}
          title={t('endConversation', language)}
        >
          <X size={18} weight="regular" />
        </button>
      </div>
    </div>
  );
});

VoiceBar.displayName = 'VoiceBar';

VoiceBar.propTypes = {
  isSpeaking: PropTypes.bool,
  isUserSpeaking: PropTypes.bool,
  connectionStatus: connectionStatusPropType,
  onClose: PropTypes.func.isRequired,
  onExpandToChat: PropTypes.func,
  language: languagePropType,
  glassEffect: PropTypes.bool,
  primaryColor: PropTypes.string,
  theme: PropTypes.shape({
    variant: PropTypes.oneOf(['light', 'dark']),
    primaryColor: PropTypes.string,
    accentContrastColor: PropTypes.string,
  }),
  isAccentMode: PropTypes.bool,
};
